import {
  HttpApi,
  HttpApiEndpoint,
  HttpApiSchema,
  OpenApi,
} from 'effect/http-api';
import { Schema } from 'effect';
import { adminGroup } from './admin-endpoints';
import { analyticsGroup } from './analytics-endpoints';
import { audioGroup } from './audio-endpoints';
import { billingGroup } from './billing-endpoints';
import { blogGroup } from './blog-endpoints';
import { chapterGroup } from './chapter-endpoints';
import { flagsGroup } from './flags-endpoints';
import { folderGroup } from './folder-endpoints';
import { notesGroup } from './notes-endpoints';
import { ocrGroup } from './ocr-endpoints';
import { pdfGroup } from './pdf-endpoints';
import { emailGroup } from './email-endpoints';
import { eventsGroup } from './events-endpoints';
import { mobileAuthGroup } from './mobile-auth-endpoints';
import { mobileReleaseGroup } from './mobile-release-endpoints';
import { mobileUpdatesGroup } from './mobile-updates-endpoints';
import {
  moneyGroup,
  paymentsGroup,
  stripeGroup,
  webhooksGroup,
} from './payments-endpoints';
import { ttsGroup } from './tts-endpoints';
import { userGroup } from './user-endpoints';
import { voicesGroup } from './voices-endpoints';
import { publicDocGroup } from './public-doc-endpoints';
import { publicToolsGroup } from './public-tools-endpoints';
import { supportChatGroup } from './support-chat-endpoints';
import { businessContactGroup } from './business-contact-endpoints';
import { zendeskGroup } from './zendesk-endpoints';
import { dropboxGroup } from './dropbox';
import { googleDriveGroup } from './google-drive';
import { zoteroGroup } from './zotero-endpoints';
import { InternalServerError } from './errors';
import {
  ApiConnectionMiddleware,
  RequestDecodeErrorMiddleware,
  UserAuthMiddleware,
} from './middleware';

export type { SubscriptionHistoryRow } from './admin-endpoints';
export { StripeSubscriptionViewWithPricingVariant } from './payments-endpoints';
export {
  AnnouncementId,
  UpdateProfileRequest,
  UpdatePreferencesRequest,
  FeedbackRequest,
  PostCancelSurveyRequest,
  SummaryResponse,
  UserProfileData,
} from './user-endpoints';
export {
  NoteResponse,
  NoteUpsertInput,
  ListNotesUrlParams,
} from './notes-endpoints';
export {
  ListAudioUrlParams,
  MAX_FINALIZATION_STATUS_IDS,
  OfflineDocPackBuild,
  UpdateAudioPayload,
} from './audio-endpoints';
export { BootstrapResponse } from './analytics-endpoints';
export {
  AudioChanged,
  Connected,
  ChangeEvent,
  KeepAlive,
  LiveEvent,
  ProgressChanged,
  Resync,
} from './events-endpoints';
export {
  BlogPostSummary,
  GetPostResponse,
  ListPostsResponse,
} from './blog-endpoints';
export { PublicDocPromotion } from './public-doc-promotion';
export { MAX_AUDIO_CONVERSIONS_PER_FOLDER_UPDATE } from './folder-endpoints';
export { DEMO_TEXT_MAX_LENGTH } from './tts-endpoints';
export {
  zoteroAttachmentDocumentType,
  DOWNLOADABLE_ZOTERO_LINK_MODES,
  isImportableZoteroAttachment,
  zoteroItemImportability,
  type ZoteroImportability,
  ZoteroCollectionItem,
  ZoteroItemSummary,
} from './zotero-endpoints';
export {
  PUBLIC_TOOL_TEXT_MAX_LENGTH,
  PUBLIC_TOOL_PDF_MAX_LENGTH,
  PUBLIC_TOOL_PDF_MAX_PAGES,
  PUBLIC_TOOL_PDF_MAX_FILE_SIZE_BYTES,
  PUBLIC_TOOL_WEB_MAX_LENGTH,
  PUBLIC_TOOL_UPLOAD_DOCUMENT_TYPES,
  PublicToolJobResult,
  PublicToolUploadDocumentType,
  isPublicToolUploadDocumentType,
} from './public-tools-endpoints';
export {
  ListPublicDocsResponse,
  PUBLIC_DOCS_DEFAULT_LIMIT,
  PUBLIC_DOCS_MAX_LIMIT,
} from './public-doc-endpoints';
export {
  CATEGORY_GROUPS,
  DOCUMENT_CATEGORIES,
  DOCUMENT_CATEGORY_MAP,
  PUBLIC_DOC_MAIN_CATEGORIES,
  resolvePublicDocCategory,
  type DocumentCategory,
} from './public-doc-categories';
export { PUBLIC_DOC_CATEGORY_SLUGS } from './public-doc-category-slugs';
export {
  FREE_PLAN_COUNTRY_CODES,
  MAX_FREE_UPLOADS,
  freeUploadWeekStartMs,
  isFreePlanCountryCode,
  nextFreeUploadResetMs,
} from './free-plan-policy';

// Re-export for consumers
export {
  CurrentSession,
  UserAuthMiddleware,
  AdminAuthMiddleware,
  OptionalUserAuthMiddleware,
  RequestDecodeErrorMiddleware,
  ApiConnectionMiddleware,
  ApiUnreachableError,
} from './middleware';
export {
  AudioRechunkingError,
  DecodeIssueSymbolKey,
  HttpApiDecodeError,
  InternalServerError,
  DocumentInsightsFailedError,
  BadRequestError,
  UnauthorizedError,
  NotFoundError,
  ConflictError,
  ForbiddenError,
  FileTooLargeError,
  DocumentInaccessibleError,
  UploadDocumentTypeNotAllowedError,
  UploadQuotaExceededError,
  TooManyRequestsError,
  RateLimitedError,
  CaptchaError,
  BadGatewayError,
  ServiceUnavailableError,
} from './errors';

export const BASE_PREFIX = '/api';

// Extend userGroup with additional endpoints not in the legacy API
const userExtensionsGroup = userGroup.add(
  HttpApiEndpoint.post('ArchiveUser', '/archive', {
    success: Schema.Void.pipe(HttpApiSchema.status(200)),
    error: InternalServerError,
  })
    .middleware(UserAuthMiddleware)
    .annotate(OpenApi.Summary, 'Archive User')
    .annotate(
      OpenApi.Description,
      'Archive a user. This will archive all audios in a users account.',
    ),
);

export const ApiHttpApiContract = HttpApi.make('api')
  .annotate(OpenApi.Description, 'Listening API')
  .add(adminGroup)
  .add(analyticsGroup)
  .add(audioGroup)
  .add(billingGroup)
  .add(blogGroup)
  .add(businessContactGroup)
  .add(chapterGroup)
  .add(emailGroup)
  .add(eventsGroup)
  .add(flagsGroup)
  .add(folderGroup)
  .add(mobileAuthGroup)
  .add(mobileReleaseGroup)
  .add(mobileUpdatesGroup)
  .add(moneyGroup)
  .add(notesGroup)
  .add(ocrGroup)
  .add(paymentsGroup)
  .add(pdfGroup)
  .add(publicDocGroup)
  .add(publicToolsGroup)
  .add(stripeGroup)
  .add(supportChatGroup)
  .add(ttsGroup)
  .add(voicesGroup)
  .add(userExtensionsGroup)
  .add(webhooksGroup)
  .add(zendeskGroup)
  .add(zoteroGroup)
  .add(dropboxGroup)
  .add(googleDriveGroup)
  // Restores v3's implicit per-endpoint HttpApiDecodeError declaration — see
  // RequestDecodeErrorMiddleware. Must stay AFTER every .add(): HttpApi
  // .middleware only applies to endpoints already present.
  .middleware(RequestDecodeErrorMiddleware)
  .middleware(ApiConnectionMiddleware)
  .prefix(BASE_PREFIX);
